import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { ApiClient } from './client';
import { TokenManager } from '../../auth/tokenManager';

interface ApiProviderProps {
  children: React.ReactNode;
  baseURL?: string;
}

interface ApiContextValue {
  client: ApiClient;
}

const ApiContext = createContext<ApiContextValue | null>(null);

export function ApiProvider({ children, baseURL = '/api' }: ApiProviderProps) {
  const { instance, accounts } = useMsal();

  // Initialize TokenManager with MSAL instance
  const tokenManager = useMemo(
    () => TokenManager.getInstance(instance),
    [instance]
  );

  // Clear token cache on logout
  useEffect(() => {
    if (accounts.length === 0) {
      tokenManager.clearCache();
    }
  }, [accounts, tokenManager]);

  const client = useMemo(() => {
    const getToken = async () => {
      if (!accounts[0]) {
        throw new Error('No active account');
      }

      try {
        // Use TokenManager to prevent race conditions
        return await tokenManager.getToken({
          scopes: ['User.Read', 'openid', 'profile', 'email'],
          account: accounts[0],
        });
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          console.log(
            '[ApiProvider] Interactive login required, redirecting...'
          );
          instance.loginRedirect();
          throw error;
        }
        throw error;
      }
    };

    return new ApiClient({
      baseURL,
      getToken,
      onAuthError: () => {
        tokenManager.clearCache();
        instance.loginRedirect();
      },
    });
  }, [instance, accounts, baseURL, tokenManager]);

  return (
    <ApiContext.Provider value={{ client }}>{children}</ApiContext.Provider>
  );
}

export function useApi() {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context.client;
}
